// การเข้าเรียนรายบุคคล (ภาคศาสนา) — หัวหน้าวิชาการศาสนา (pos 57)
//
// คู่ขนานกับ reports/AttendanceTrackingStudent.js ของภาคสามัญ สองแท็บเหมือนกัน:
//   รายบุคคล   ค้นหานักเรียนหนึ่งคน แล้วเห็นทุกคาบที่ถูกเช็คชื่อ + สรุปรายวิชา
//   กลุ่มเสี่ยง  ไล่จากคนที่ขาดมากที่สุดในช่วงที่เลือก แล้วกดเข้าไปดูรายคาบได้
//
// ต่างจากของภาคสามัญสองเรื่อง ทั้งสองเรื่องมาจากบริบทของภาคศาสนาโดยตรง:
//   - แถวที่ถูกกาขาดทั้งที่มีใบลาครอบอยู่ติดป้ายเตือน (แหล่งข้อมูลเดียวกับหน้าติดตาม
//     นักเรียนขาดเรียน) เพราะสี่ฝ่ายแจ้งลาลงตารางเดียวกัน ครูผู้สอนศาสนาจึงพลาดใบลา
//     ที่ฝ่ายอื่นแจ้งได้ง่าย — คนที่ต้องแก้คือครู ไม่ใช่ปล่อยให้นักเรียนรับผลว่าขาด
//   - กดจากแท็บกลุ่มเสี่ยงแล้วช่วงวันที่ตามไปด้วย ตัวเลขที่เห็นในสองแท็บจึงตรงกันเสมอ
//
// อ่านอย่างเดียว การแก้การเช็คชื่อเป็นหน้าที่ของครูผู้สอน
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Form, InputGroup, Row, Table } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'
import axiosInstance from '../services/axiosInterceptor'
import { useLoading } from '../contexts/LoadingContext'
import { useLanguage } from '../contexts/LanguageContext'
import { StudentName, studentDisplayName } from '../utils/studentName'
import th from '../locales/islamic/th'
import jawi from '../locales/islamic/jawi'
import ThaiDateInput from '../components/ThaiDateInput'
import {
  API, ATT_STATUS, AcademicFilterBar, DateRangeBar, StatTile, StatusDot, StudentAvatar,
  YearSelect, alertRequestError, roomLabel, thaiDate, useAcademicFilters, useDarkTables,
  useDateRange, useStudyYears,
} from './academicShared'

const STATUS_COLOR = ATT_STATUS.reduce((m, s) => ({ ...m, [s.key]: s.color }), {})

// ป้ายสถานะของแถวหนึ่ง — สีอยู่ที่จุดนำหน้า ตัวอักษรคงสีตามธีม (เหลือง/ฟ้าคอนทราสต์
// ต่ำเกินกว่าจะเป็นสีตัวอักษรให้อ่านออกทั้งสองธีม เหตุผลเดียวกับ StatTile)
const StatusText = ({ t, status }) => (
  <span className="d-inline-flex align-items-center gap-1">
    <StatusDot color={STATUS_COLOR[status] || '#adb5bd'} size={8} />
    {t.academic.attendance[status] || status || '-'}
  </span>
)

// ป้าย "มีใบลา" บนแถวที่ถูกกาขาด — บอกทั้งประเภทและฝ่ายที่แจ้ง เพราะ "ครูประจำชั้นศาสนา
// แจ้งแต่ครูผู้สอนกาขาด" กับ "ฝ่ายปกครองอนุมัติใบกลับบ้านแล้วแต่ยังถูกกาขาด" ต้องไปตาม
// คนละที่ (ป้ายชุดเดียวกับหน้าติดตามนักเรียนขาดเรียน)
const LeaveFlag = ({ t, row }) => {
  if (!row.leave_type) return null
  const type = t.academic.leave.type[row.leave_type] || row.leave_type
  const dept = t.academic.leave.source[row.leave_source]
  // ใบกลับบ้านชื่อประเภทกับชื่อฝ่ายเป็นเรื่องเดียวกัน เขียนคู่กันได้ "กลับบ้าน · ใบกลับบ้าน"
  const label = !dept ? type
    : row.leave_source === 'home_leave' ? dept
    : `${type} · ${dept}`
  return (
    <Badge bg="warning" text="dark" className="fs-xs ms-1">
      <i className="ri-information-line me-1" />
      {t.academic.individual.hasLeave(label)}
    </Badge>
  )
}

const rate = (present, late, total) => (total > 0 ? Math.round(((present + late) / total) * 100) : 0)

export default function IslamicAttendanceIndividual() {
  const { setLoading } = useLoading()
  const { lang } = useLanguage()
  const t = lang === 'th' ? th : jawi
  const navigate = useNavigate()
  useDarkTables()

  const { years, yearId, setYearId } = useStudyYears()
  // ตัวกรองชั้น/ห้อง/เพศ ใช้เฉพาะแท็บกลุ่มเสี่ยง ซึ่งเป็นรายการทั้งภาคที่ต้องหุบให้แคบลง
  // แท็บรายบุคคลชี้ตัวนักเรียนจากชื่อ/เลขประจำตัวตรง ๆ อยู่แล้ว
  const filters = useAcademicFilters(yearId, t)
  const [tab, setTab] = useState('individual')

  // ---- แท็บรายบุคคล ----
  const [q, setQ] = useState('')
  const [results, setResults] = useState(null)   // null = ยังไม่ได้ค้น, [] = ค้นแล้วไม่เจอ
  const [searching, setSearching] = useState(false)
  const [studentId, setStudentId] = useState(null)
  const [data, setData] = useState(null)
  const [notFound, setNotFound] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [dateError, setDateError] = useState('')

  // ---- แท็บกลุ่มเสี่ยง ----
  const range = useDateRange('month')
  const [minAbsent, setMinAbsent] = useState(3)
  const [risk, setRisk] = useState(null)

  const handleSearch = (e) => {
    e.preventDefault()
    const key = q.trim()
    if (key.length < 2 || !yearId) return
    setSearching(true)
    // ไม่ส่งตัวกรองของแท็บกลุ่มเสี่ยงมาด้วย ไม่งั้นค่าที่ครูตั้งไว้อีกแท็บหนึ่งจะหุบผลการค้นหา
    // อย่างเงียบ ๆ — ค้นชื่อที่มีอยู่จริงแล้วขึ้นว่าไม่พบ
    const p = new URLSearchParams()
    p.set('q', key)
    axiosInstance.get(`${API}/islamic/academic/searchStudents/${yearId}?${p}`)
      .then(res => setResults(Array.isArray(res.data) ? res.data : []))
      .catch(err => { setResults([]); alertRequestError(t, err, t.common.error) })
      .finally(() => setSearching(false))
  }

  const openStudent = useCallback((stId, withRange) => {
    setStudentId(stId)
    setResults(null)
    setNotFound(false)
    if (withRange) { setFrom(withRange.start); setTo(withRange.end) }
    setTab('individual')
  }, [])

  // ระบุวันที่มาข้างเดียวคือกรอกไม่ครบ — บอกตรงนั้น ไม่ยิงคำขอที่ server จะตอบ 400
  const bothOrNone = (!from && !to) || (from && to)

  // ไม่ส่งตัวกรองชั้น/ห้อง/เพศ ไปกับคำขอนี้ — เปิดนักเรียนคนหนึ่งไว้แล้วครูไปเปลี่ยน
  // ตัวกรองข้างบน ไม่ควรทำให้หน้าที่เปิดอยู่กลายเป็น "ไม่พบนักเรียน"
  useEffect(() => {
    if (!yearId || !studentId) { setData(null); return }
    if (!bothOrNone) { setDateError(t.academic.individual.needBothDates); return }
    setDateError('')
    let alive = true
    setLoading(true)
    const p = new URLSearchParams()
    if (from && to) { p.set('start', from); p.set('end', to) }
    axiosInstance.get(`${API}/islamic/academic/studentAttendance/${studentId}/${yearId}?${p}`)
      .then(res => { if (alive) { setData(res.data); setNotFound(false) } })
      .catch(err => {
        if (!alive) return
        setData(null)
        if (err.response?.status === 404) setNotFound(true)
        else alertRequestError(t, err, t.common.error)
      })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearId, studentId, from, to])

  useEffect(() => {
    if (tab !== 'risk' || !yearId) return
    let alive = true
    setLoading(true)
    const p = new URLSearchParams(filters.query.replace(/^\?/, ''))
    p.set('start', range.start)
    p.set('end', range.end)
    if (minAbsent) p.set('minAbsent', minAbsent)
    axiosInstance.get(`${API}/islamic/academic/atRisk/${yearId}?${p}`)
      .then(res => { if (alive) setRisk(res.data?.rows || []) })
      .catch(err => { if (alive) { setRisk([]); alertRequestError(t, err, t.common.error) } })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [tab, yearId, filters.query, range.start, range.end, minAbsent, setLoading, t])

  // ห่อ useMemo เพราะ [] ใหม่ทุกรอบ render จะทำให้ useMemo ข้างล่างคำนวณใหม่ทุกครั้ง
  const rows = useMemo(() => data?.rows || [], [data])
  const student = data?.student || null

  const summary = useMemo(() => {
    const acc = { present: 0, late: 0, leave: 0, absent: 0, total: 0, withLeave: 0 }
    for (const r of rows) {
      if (acc[r.att_status] !== undefined) acc[r.att_status] += 1
      if (r.leave_type) acc.withLeave += 1
      acc.total += 1
    }
    return acc
  }, [rows])

  // สรุปรายวิชา — จัดกลุ่มที่ฝั่งหน้าเว็บ ไม่ใช่ยิงคำขอเพิ่ม เพราะแถวทั้งชุดอยู่ในมือแล้ว
  const bySubject = useMemo(() => {
    const map = new Map()
    for (const r of rows) {
      const key = r.sub_subid || r.sub_subname || '-'
      if (!map.has(key)) {
        map.set(key, {
          subid: r.sub_subid, subname: r.sub_subname, teachers: new Set(),
          present: 0, late: 0, leave: 0, absent: 0, total: 0,
        })
      }
      const s = map.get(key)
      if (r.t_name) s.teachers.add(r.t_name)
      if (s[r.att_status] !== undefined) s[r.att_status] += 1
      s.total += 1
    }
    return [...map.values()]
      .map(s => ({ ...s, teachers: [...s.teachers].join(', ') }))
      .sort((a, b) => b.absent - a.absent || b.total - a.total)
  }, [rows])

  // ไฟล์ที่สั่งออกเป็นภาษาไทยเสมอ ไม่ตามภาษาเมนู — เป็นเอกสารที่ส่งต่อให้ฝ่ายอื่นและ
  // เข้าแฟ้มนักเรียน กฎเดียวกับเอกสารสั่งพิมพ์อื่นของภาคศาสนา
  const exportExcel = () => {
    if (!student) return
    const sheet = [
      ['รายงานการเข้าเรียนรายบุคคล (ภาคศาสนา)'],
      [`${student.st_idnumber} ${student.st_name}`],
      [`ชั้นศาสนา ${roomLabel(student)}${student.regular_class ? ` · ชั้นสามัญ ${student.regular_class}` : ''}`],
      [`ช่วงวันที่: ${from && to ? `${from} ถึง ${to}` : 'ทั้งปีการศึกษา'}`],
      [`สรุป: มา ${summary.present} / สาย ${summary.late} / ลา ${summary.leave} / ขาด ${summary.absent}`
        + ` (เข้าเรียน ${rate(summary.present, summary.late, summary.total)}%)`],
      [],
      ['#', 'วันที่', 'คาบ', 'วิชา', 'ครูผู้สอน', 'หัวข้อที่สอน', 'สถานะ', 'ใบลา', 'ผู้แจ้งลา'],
    ]
    rows.forEach((r, i) => sheet.push([
      i + 1,
      r.att_date || '-',
      r.period_order ?? '-',
      r.sub_subname ? `${r.sub_subid || ''} ${r.sub_subname}`.trim() : '-',
      r.t_name || '-',
      r.tr_topic || '-',
      th.academic.attendance[r.att_status] || r.att_status,
      r.leave_type ? (th.academic.leave.type[r.leave_type] || r.leave_type) : '',
      // ฝ่ายที่แจ้งใบลา — ไฟล์ที่ส่งต่อให้คนอื่นอ่านต้องบอกได้เองว่าต้องไปตามเรื่องที่ใคร
      r.leave_source ? (th.academic.leave.source[r.leave_source] || r.leave_source) : '',
    ]))
    const ws = XLSX.utils.aoa_to_sheet(sheet)
    ws['!cols'] = [{ wch: 5 }, { wch: 12 }, { wch: 6 }, { wch: 28 }, { wch: 22 }, { wch: 32 }, { wch: 10 }, { wch: 14 }, { wch: 18 }]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'การเข้าเรียน')
    XLSX.writeFile(wb, `การเข้าเรียนศาสนา_${student.st_idnumber}.xlsx`)
  }

  const tx = t.academic.individual

  return (
    <React.Fragment>
      <div className="mb-3">
        <ol className="breadcrumb fs-sm mb-1">
          <li className="breadcrumb-item">{t.academic.groupTitle}</li>
          <li className="breadcrumb-item active">{tx.pageTitle}</li>
        </ol>
        <h4 className="main-title mb-0">{tx.pageTitle}</h4>
      </div>

      <div className="btn-group btn-group-sm mb-3" role="group">
        <button
          type="button"
          className={`btn btn-${tab === 'individual' ? 'primary' : 'outline-secondary'}`}
          onClick={() => setTab('individual')}
        >
          <i className="ri-user-line me-1" />{tx.tabIndividual}
        </button>
        <button
          type="button"
          className={`btn btn-${tab === 'risk' ? 'primary' : 'outline-secondary'}`}
          onClick={() => setTab('risk')}
        >
          <i className="ri-alarm-warning-line me-1" />{tx.tabRisk}
        </button>
      </div>

      {tab === 'individual' ? (
        <>
          <Card className="card-one mb-3">
            <Card.Body>
              <Form onSubmit={handleSearch}>
                <Row className="g-2 align-items-end">
                  <YearSelect t={t} years={years} yearId={yearId} setYearId={setYearId} />
                  <Col xs={12} md={6} lg={4}>
                    <Form.Label className="fs-xs text-secondary mb-1">{tx.search}</Form.Label>
                    <InputGroup size="sm">
                      <Form.Control
                        value={q}
                        onChange={e => setQ(e.target.value)}
                        placeholder={tx.searchPlaceholder}
                      />
                      <Button type="submit" variant="primary" disabled={searching || q.trim().length < 2}>
                        <i className="ri-search-2-line me-1" />{tx.searchBtn}
                      </Button>
                    </InputGroup>
                  </Col>
                </Row>
              </Form>

              <hr className="my-3" />
              <Row className="g-2 align-items-end">
                <Col xs={6} md={3} lg={2}>
                  <Form.Label className="fs-xs text-secondary mb-1">{tx.from}</Form.Label>
                  <ThaiDateInput size="sm" value={from} onChange={v => setFrom(v)} />
                </Col>
                <Col xs={6} md={3} lg={2}>
                  <Form.Label className="fs-xs text-secondary mb-1">{tx.to}</Form.Label>
                  <ThaiDateInput size="sm" value={to} onChange={v => setTo(v)} />
                </Col>
                <Col xs="auto">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={!from && !to}
                    onClick={() => { setFrom(''); setTo('') }}
                  >
                    {tx.wholeYear}
                  </Button>
                </Col>
                <Col className="fs-xs text-secondary">{tx.rangeHint}</Col>
              </Row>
              {dateError && <div className="fs-xs text-danger mt-2">{dateError}</div>}

              {results !== null && (
                results.length === 0 ? (
                  <Alert variant="warning" className="mt-3 mb-0 py-2 fs-sm">{tx.noResult}</Alert>
                ) : (
                  <div className="mt-3">
                    <div className="fs-xs text-secondary mb-2">
                      {tx.resultCount(results.length)}
                      {results.length >= 30 && <span className="ms-1">{tx.resultCapped}</span>}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {results.map(s => (
                        <Button
                          key={s.is_study_id}
                          size="sm"
                          variant="outline-primary"
                          className="d-inline-flex align-items-center gap-2"
                          onClick={() => openStudent(s.st_id)}
                        >
                          <StudentAvatar photo={s.st_photo} name={studentDisplayName(s, lang)} size={22} />
                          <span><StudentName student={s} lang={lang} /></span>
                          <span className="text-secondary fs-xs">{s.st_idnumber} · {roomLabel(s)}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                )
              )}
            </Card.Body>
          </Card>

          {notFound && <Alert variant="warning" className="py-2 fs-sm">{tx.notFound}</Alert>}

          {!student ? (
            !notFound && (
              <Card className="card-one">
                <Card.Body className="text-center text-secondary py-5 fs-sm">{tx.pickStudent}</Card.Body>
              </Card>
            )
          ) : (
            <>
              <Card className="card-one mb-3">
                <Card.Body className="p-3 d-flex flex-wrap align-items-center gap-3">
                  <StudentAvatar photo={student.st_photo} name={studentDisplayName(student, lang)} size={48} />
                  <div style={{ minWidth: 0 }}>
                    <div className="fw-semibold"><StudentName student={student} lang={lang} /></div>
                    <div className="fs-xs text-secondary">{student.st_idnumber}</div>
                  </div>
                  <Badge bg="info">{roomLabel(student)}</Badge>
                  {student.regular_class && (
                    <span className="fs-sm text-secondary">{tx.regularClass} {student.regular_class}</span>
                  )}
                  {student.dorm_name && (
                    <span className="fs-sm text-secondary">{tx.dorm} {student.dorm_name}</span>
                  )}
                  {student.transfer_room_id && (
                    <Badge bg="secondary" className="fs-xs">{tx.moved}</Badge>
                  )}
                  {student.st_status === '0' && (
                    <Badge bg="danger" className="fs-xs">{t.academic.studentList.status.withdrawn}</Badge>
                  )}
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="ms-auto"
                    onClick={() => navigate(`/islamic/student-tracking/${student.st_id}`)}
                  >
                    <i className="ri-radar-line me-1" />{tx.trackingBtn}
                  </Button>
                </Card.Body>
              </Card>

              <div className="row g-2 mb-3">
                {ATT_STATUS.map(({ key, color }) => (
                  <Col xs={6} md={3} lg={2} key={key}>
                    <StatTile
                      color={color}
                      label={t.academic.attendance[key]}
                      value={summary[key].toLocaleString('th-TH')}
                    />
                  </Col>
                ))}
                <Col xs={6} md={3} lg={2}>
                  <StatTile
                    label={tx.statRate}
                    value={`${rate(summary.present, summary.late, summary.total)}%`}
                    sub={tx.rateHint}
                  />
                </Col>
                <Col xs={6} md={3} lg={2}>
                  <StatTile
                    color={summary.withLeave > 0 ? '#ffc107' : undefined}
                    label={t.academic.absence.statWithLeave}
                    value={summary.withLeave.toLocaleString('th-TH')}
                    sub={summary.withLeave > 0 ? t.academic.absence.withLeaveHint : null}
                  />
                </Col>
              </div>

              {bySubject.length > 0 && (
                <Card className="card-one mb-3">
                  <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                    <Card.Title as="h6" className="mb-0">{tx.bySubjectTitle}</Card.Title>
                    <Badge bg="secondary" className="fs-xs">{tx.subjectCount(bySubject.length)}</Badge>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table className="table-sm mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>{tx.colSubject}</th>
                            <th style={{ width: 180 }}>{tx.colTeacher}</th>
                            <th className="text-end" style={{ width: 80 }}>{t.academic.attendance.absent}</th>
                            <th className="text-end" style={{ width: 80 }}>{t.academic.attendance.late}</th>
                            <th className="text-end" style={{ width: 80 }}>{t.academic.attendance.leave}</th>
                            <th className="text-end" style={{ width: 80 }}>{t.academic.attendance.present}</th>
                            <th className="text-end" style={{ width: 70 }}>{tx.colTotal}</th>
                            <th className="text-end" style={{ width: 90 }}>{tx.colRate}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bySubject.map((s, i) => (
                            <tr key={i} className={s.absent > 0 ? 'table-warning' : undefined}>
                              <td className="fs-sm">
                                {s.subname
                                  ? <>{s.subid ? <span className="text-secondary me-1">{s.subid}</span> : null}{s.subname}</>
                                  : <span className="text-secondary">-</span>}
                              </td>
                              <td className="fs-sm">{s.teachers || <span className="text-secondary">-</span>}</td>
                              <td className="text-end fs-sm fw-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.absent}</td>
                              <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.late}</td>
                              <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.leave}</td>
                              <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.present}</td>
                              <td className="text-end fs-sm text-secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.total}</td>
                              <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {rate(s.present, s.late, s.total)}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              )}

              <Card className="card-one">
                <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                  <Card.Title as="h6" className="mb-0">{tx.detailTitle}</Card.Title>
                  <Badge bg="secondary" className="fs-xs">{tx.recordCount(rows.length)}</Badge>
                  {rows.length > 0 && (
                    <Button size="sm" variant="outline-success" className="ms-auto" onClick={exportExcel}>
                      <i className="ri-file-excel-2-line me-1" />{tx.excel}
                    </Button>
                  )}
                </Card.Header>
                <Card.Body className="p-0">
                  {rows.length === 0 ? (
                    <div className="text-center text-secondary py-5 fs-sm">{tx.empty}</div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table-sm mb-0 align-middle table-hover">
                        <thead>
                          <tr>
                            <th style={{ width: 130 }}>{tx.colDate}</th>
                            <th style={{ width: 60 }}>{tx.colPeriod}</th>
                            <th>{tx.colSubject}</th>
                            <th style={{ width: 160 }}>{tx.colTeacher}</th>
                            <th>{tx.colTopic}</th>
                            <th style={{ width: 150 }}>{tx.colStatus}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r, i) => (
                            <tr key={i} className={r.leave_type ? 'table-warning' : undefined}>
                              <td className="fs-sm">{thaiDate(r.att_date)}</td>
                              <td className="fs-sm">{r.period_order ?? '-'}</td>
                              <td className="fs-sm">
                                {r.sub_subname
                                  ? <>{r.sub_subid ? <span className="text-secondary me-1">{r.sub_subid}</span> : null}{r.sub_subname}</>
                                  : <span className="text-secondary">-</span>}
                              </td>
                              <td className="fs-sm">{r.t_name || <span className="text-secondary">-</span>}</td>
                              <td className="fs-sm">
                                {r.tr_topic || <span className="text-secondary fst-italic">{tx.noTopic}</span>}
                              </td>
                              <td className="fs-sm">
                                <StatusText t={t} status={r.att_status} />
                                <LeaveFlag t={t} row={r} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
                <Card.Footer className="fs-xs text-secondary">{tx.detailFooter}</Card.Footer>
              </Card>
            </>
          )}
        </>
      ) : (
        <>
          <Card className="card-one mb-3">
            <Card.Body>
              <DateRangeBar t={t} range={range} />
              <hr className="my-3" />
              <AcademicFilterBar t={t} years={years} yearId={yearId} setYearId={setYearId} filters={filters}>
                <Col xs={6} md={3} lg={2}>
                  <Form.Label className="fs-xs text-secondary mb-1">{tx.minAbsent}</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    min={1}
                    value={minAbsent}
                    onChange={e => setMinAbsent(e.target.value)}
                  />
                </Col>
              </AcademicFilterBar>
            </Card.Body>
          </Card>

          <Card className="card-one">
            <Card.Header className="d-flex flex-wrap align-items-center gap-2">
              <Card.Title as="h6" className="mb-0">{tx.riskTitle}</Card.Title>
              <Badge bg="secondary" className="fs-xs">{t.common.personCount((risk || []).length)}</Badge>
              {(risk || []).length >= 500 && <span className="fs-xs text-secondary">{tx.riskCapped}</span>}
            </Card.Header>
            <Card.Body className="p-0">
              {risk && risk.length === 0 ? (
                <div className="text-center text-secondary py-5 fs-sm">{tx.riskEmpty}</div>
              ) : (
                <div className="table-responsive">
                  <Table className="table-sm mb-0 align-middle table-hover">
                    <thead>
                      <tr>
                        <th style={{ width: 48 }}>#</th>
                        <th>{t.academic.attendance.colStudent}</th>
                        <th style={{ width: 100 }}>{t.academic.attendance.colRoom}</th>
                        <th className="text-end" style={{ width: 80 }}>{t.academic.attendance.absent}</th>
                        <th className="text-end" style={{ width: 80 }}>{t.academic.attendance.late}</th>
                        <th className="text-end" style={{ width: 80 }}>{t.academic.attendance.leave}</th>
                        <th className="text-end" style={{ width: 80 }}>{t.academic.attendance.present}</th>
                        <th className="text-end" style={{ width: 70 }}>{tx.colTotal}</th>
                        <th className="text-end" style={{ width: 90 }}>{tx.colRate}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(risk || []).map((s, i) => (
                        <tr
                          key={`${s.st_id}-${s.ir_id}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => openStudent(s.st_id, { start: range.start, end: range.end })}
                        >
                          <td className="text-secondary fs-xs">{i + 1}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <StudentAvatar photo={s.st_photo} name={studentDisplayName(s, lang)} size={28} />
                              <div style={{ minWidth: 0 }}>
                                <div className="fs-sm text-truncate"><StudentName student={s} lang={lang} /></div>
                                <div className="fs-xs text-secondary">{s.st_idnumber}</div>
                              </div>
                            </div>
                          </td>
                          <td className="fs-sm">{roomLabel(s)}</td>
                          <td className="text-end fs-sm fw-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.absent}</td>
                          <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.late}</td>
                          <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.leave}</td>
                          <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.present}</td>
                          <td className="text-end fs-sm text-secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.total}</td>
                          <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {rate(s.present, s.late, s.total)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
            <Card.Footer className="fs-xs text-secondary">{tx.riskFooter}</Card.Footer>
          </Card>
        </>
      )}
    </React.Fragment>
  )
}
